import { useMemo, useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import TraderModal from "./features/traders/components/TraderModal";
import { LEGAL_LANGUAGE_DA, LEGAL_LANGUAGE_EN, getLegalUiText, getPrivacyDocument, getTermsDocument } from "./legalDocuments";
import { useAuth } from "./authContext";

const MODE_LOGIN = "login";
const MODE_REGISTER = "register";
const USERNAME_MAX_LENGTH = 20;
const USERNAME_PATTERN = /^[A-Za-z0-9-]+$/;
const LEGAL_MODAL_TYPES = Object.freeze({
  TERMS: "terms",
  PRIVACY: "privacy"
});

function getRedirectTarget(location) {
  return location.state?.from?.pathname || "/account";
}

export default function AuthView() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, isLoading, login, register, requestEmailVerification } = useAuth();
  const initialMode = location.state?.authMode === MODE_REGISTER ? MODE_REGISTER : MODE_LOGIN;
  const [mode, setMode] = useState(initialMode);
  const [formState, setFormState] = useState({
    identifier: "",
    username: "",
    email: "",
    password: "",
    acceptedLegalTerms: false
  });
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [devUrl, setDevUrl] = useState("");
  const [verificationIdentifier, setVerificationIdentifier] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeLegalModal, setActiveLegalModal] = useState(null);
  const [activeLegalLanguage, setActiveLegalLanguage] = useState(LEGAL_LANGUAGE_DA);

  const redirectTarget = useMemo(() => getRedirectTarget(location), [location]);

  if (!isLoading && isAuthenticated) {
    return <Navigate to={redirectTarget} replace />;
  }

  const submitLabel = mode === MODE_LOGIN ? "Log in" : "Create account";
  const legalUiText = getLegalUiText(activeLegalLanguage);
  const activeLegalDocument = activeLegalModal === LEGAL_MODAL_TYPES.TERMS
    ? getTermsDocument(activeLegalLanguage)
    : activeLegalModal === LEGAL_MODAL_TYPES.PRIVACY
      ? getPrivacyDocument(activeLegalLanguage)
      : null;

  const handleChange = (field) => (event) => {
    setFormState((currentState) => ({
      ...currentState,
      [field]: event.target.value
    }));
  };

  const handleCheckboxChange = (field) => (event) => {
    setFormState((currentState) => ({
      ...currentState,
      [field]: event.target.checked
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setNotice("");
    setDevUrl("");
    setIsSubmitting(true);

    try {
      if (mode === MODE_LOGIN) {
        await login({
          identifier: formState.identifier,
          password: formState.password
        });

        navigate(redirectTarget, { replace: true });
      } else {
        if (!formState.acceptedLegalTerms) {
          throw new Error("You must accept the Terms of Service and Privacy Policy.");
        }

        const normalizedUsername = String(formState.username || "").trim();

        if (normalizedUsername.length < 3) {
          throw new Error("Username must be at least 3 characters.");
        }

        if (normalizedUsername.length > USERNAME_MAX_LENGTH) {
          throw new Error(`Username must be ${USERNAME_MAX_LENGTH} characters or fewer.`);
        }

        if (!USERNAME_PATTERN.test(normalizedUsername)) {
          throw new Error("Username may only contain letters, numbers and hyphens.");
        }

        const payload = await register({
          username: normalizedUsername,
          email: formState.email,
          password: formState.password,
          acceptedLegalTerms: formState.acceptedLegalTerms
        });

        if (payload?.requiresEmailVerification) {
          setVerificationIdentifier(formState.email || formState.username);
          setNotice("Account created. Verify your email before logging in.");
          setDevUrl(payload?.verification?.url || "");
          setMode(MODE_LOGIN);
          return;
        }

        navigate(redirectTarget, { replace: true });
      }
    } catch (submitError) {
      if (submitError?.payload?.requiresEmailVerification) {
        setVerificationIdentifier(submitError.payload.email || formState.identifier);
        setNotice("");
        setDevUrl(submitError.payload?.verification?.url || "");
      }

      setError(submitError.message || `Could not ${mode}.`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResendVerification = async () => {
    setError("");
    setNotice("");
    setDevUrl("");

    try {
      const payload = await requestEmailVerification(verificationIdentifier || formState.identifier || formState.email);
      setNotice("Verification email has been prepared.");
      setDevUrl(payload?.verification?.url || "");
    } catch (requestError) {
      setError(requestError.message || "Could not resend verification email.");
    }
  };

  return (
    <div className="account-shell">
      <section className="account-settings-panel account-auth-panel">
        <div className="account-page-header account-auth-panel__header">
          <div>
            <h1 className="account-page-title">Account Access</h1>
            <p className="account-copy">
              Log in or register a new account. New users must verify their email before login is active.
            </p>
          </div>
        </div>

        <div className="account-auth-toggle" role="tablist" aria-label="Choose account access mode">
          <button
            type="button"
            className={`account-auth-toggle__button${mode === MODE_LOGIN ? " account-auth-toggle__button--active" : ""}`}
            aria-pressed={mode === MODE_LOGIN}
            onClick={() => {
              setMode(MODE_LOGIN);
              setError("");
            }}
          >
            Log in
          </button>
          <button
            type="button"
            className={`account-auth-toggle__button${mode === MODE_REGISTER ? " account-auth-toggle__button--active" : ""}`}
            aria-pressed={mode === MODE_REGISTER}
            onClick={() => {
              setMode(MODE_REGISTER);
              setError("");
            }}
          >
            Register
          </button>
        </div>

        <form onSubmit={handleSubmit} className="account-setting-card account-auth-card">
          {mode === MODE_LOGIN ? (
            <label className="account-auth-field">
              Username
              <input
                type="text"
                value={formState.identifier}
                onChange={handleChange("identifier")}
                placeholder="Enter your username"
                autoComplete="username"
                className="account-auth-input"
              />
            </label>
          ) : (
            <>
              <label className="account-auth-field">
                Username
                <input
                  type="text"
                  value={formState.username}
                  onChange={handleChange("username")}
                  placeholder="mytradefinder-user"
                  autoComplete="username"
                  maxLength={USERNAME_MAX_LENGTH}
                  pattern="[A-Za-z0-9-]+"
                  title={`Use only letters, numbers and hyphens, up to ${USERNAME_MAX_LENGTH} characters`}
                  className="account-auth-input"
                />
              </label>
              <label className="account-auth-field">
                Email
                <input
                  type="email"
                  value={formState.email}
                  onChange={handleChange("email")}
                  placeholder="name@example.com"
                  autoComplete="email"
                  className="account-auth-input"
                />
              </label>
              <div className="account-auth-consent">
                <input
                  type="checkbox"
                  id="register-legal-consent"
                  checked={formState.acceptedLegalTerms}
                  onChange={handleCheckboxChange("acceptedLegalTerms")}
                  className="account-auth-consent__checkbox"
                />
                <div className="account-auth-consent__copy" id="register-legal-consent-copy">
                  <div className="account-auth-consent__text">
                    <label htmlFor="register-legal-consent" className="account-auth-consent__label">
                      I agree to the
                    </label>{" "}
                    <button
                      type="button"
                      className="account-auth-consent__link"
                      onClick={() => {
                        setActiveLegalLanguage(LEGAL_LANGUAGE_DA);
                        setActiveLegalModal(LEGAL_MODAL_TYPES.TERMS);
                      }}
                    >
                      Terms of Service
                    </button>{" "}
                    <span>and</span>{" "}
                    <button
                      type="button"
                      className="account-auth-consent__link"
                      onClick={() => {
                        setActiveLegalLanguage(LEGAL_LANGUAGE_DA);
                        setActiveLegalModal(LEGAL_MODAL_TYPES.PRIVACY);
                      }}
                    >
                      Privacy Policy
                    </button>
                    <span>.</span>
                  </div>
                </div>
              </div>
            </>
          )}

          <label className="account-auth-field">
            Password
            <input
              type="password"
              value={formState.password}
              onChange={handleChange("password")}
              placeholder={mode === MODE_LOGIN ? "Password" : "Minimum 8 characters"}
              autoComplete={mode === MODE_LOGIN ? "current-password" : "new-password"}
              className="account-auth-input"
            />
          </label>

          {notice ? (
            <div className="account-notice account-auth-message">
              {notice}
            </div>
          ) : null}

          {devUrl ? (
            <div className="account-notice account-auth-message">
              Development link: <a href={devUrl}>{devUrl}</a>
            </div>
          ) : null}

          {error ? (
            <div className="account-error account-auth-message">
              {error}
            </div>
          ) : null}

          <div className="account-save-row account-auth-actions">
            <div className="account-auth-links">
              <Link to="/" className="account-auth-link">
                Back to Home
              </Link>
              {mode === MODE_LOGIN ? (
                <Link to="/auth/forgot-password" className="account-auth-link">
                  Forgot password?
                </Link>
              ) : null}
              {verificationIdentifier ? (
                <button type="button" onClick={handleResendVerification} className="account-action-button account-action-button--secondary">
                  Resend verification
                </button>
              ) : null}
            </div>
            <button
              type="submit"
              className="account-action-button"
              disabled={isSubmitting || isLoading || (mode === MODE_REGISTER && !formState.acceptedLegalTerms)}
            >
              {isSubmitting ? "Working..." : submitLabel}
            </button>
          </div>
        </form>

        <TraderModal
          isOpen={Boolean(activeLegalDocument)}
          onClose={() => setActiveLegalModal(null)}
          title={activeLegalDocument?.title || "Legal document"}
          width="860px"
        >
          {activeLegalDocument ? (
            <div className="legal-modal-document">
              <div className="legal-page__language-toggle legal-page__language-toggle--modal" role="group" aria-label="Choose document language">
                <button
                  type="button"
                  className={`legal-page__language-button${activeLegalLanguage === LEGAL_LANGUAGE_DA ? " legal-page__language-button--active" : ""}`}
                  onClick={() => setActiveLegalLanguage(LEGAL_LANGUAGE_DA)}
                  aria-pressed={activeLegalLanguage === LEGAL_LANGUAGE_DA}
                >
                  {legalUiText.languageLabels.da}
                </button>
                <button
                  type="button"
                  className={`legal-page__language-button${activeLegalLanguage === LEGAL_LANGUAGE_EN ? " legal-page__language-button--active" : ""}`}
                  onClick={() => setActiveLegalLanguage(LEGAL_LANGUAGE_EN)}
                  aria-pressed={activeLegalLanguage === LEGAL_LANGUAGE_EN}
                >
                  {legalUiText.languageLabels.en}
                </button>
              </div>
              <p className="legal-page__intro">{activeLegalDocument.intro}</p>
              <p className="legal-page__meta">{legalUiText.lastUpdatedLabel}: {activeLegalDocument.lastUpdated}</p>
              <p className="legal-page__translation-note">{legalUiText.translationNotice}</p>
              <div className="legal-page__content">{activeLegalDocument.content}</div>
            </div>
          ) : null}
        </TraderModal>
      </section>
    </div>
  );
}